/**
 * API Client
 * Axios instance with authentication interceptors and token refresh
 * Includes automatic response unwrapping for backend wrapper format
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_ROOT_URL } from '@/config/api';
import { TokenManager } from './tokenManager';
import type { RefreshTokenResponse, BackendResponse, BackendPaginatedData, PaginatedResponse } from './types';

// ===========================================
// Refresh Token State
// ===========================================
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
}> = [];

const getUnauthorizedRedirectPath = () => {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/manage')) {
        return '/manage/login';
    }

    return '/auth/signin';
};

const processQueue = (error: Error | null, token: string | null = null) => {
    failedQueue.forEach((promise) => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token);
        }
    });
    failedQueue = [];
};

// ===========================================
// Axios Instance
// ===========================================
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000,
});

// ===========================================
// Request Interceptor
// ===========================================
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = TokenManager.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ===========================================
// Response Interceptor with Token Refresh
// ===========================================
apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
            _retry?: boolean;
        };

        // Skip refresh for login/refresh endpoints
        const isAuthEndpoint =
            originalRequest?.url?.includes('/login') ||
            originalRequest?.url?.includes('/refresh') ||
            originalRequest?.url?.includes('/register');

        // Handle 401 Unauthorized
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            const refreshToken = TokenManager.getRefreshToken();

            // No refresh token - force logout
            if (!refreshToken) {
                TokenManager.clear();
                window.location.href = getUnauthorizedRedirectPath();
                return Promise.reject(error);
            }

            // Already refreshing - queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return apiClient(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Call refresh token endpoint
                const response = await axios.post<RefreshTokenResponse>(
                    `${API_ROOT_URL}/auth/refresh`,
                    { refresh_token: refreshToken },
                    { headers: { 'Content-Type': 'application/json' } }
                );

                const { access_token, refresh_token: newRefreshToken } = response.data;

                TokenManager.setToken(access_token);
                if (newRefreshToken) {
                    TokenManager.setRefreshToken(newRefreshToken);
                }

                processQueue(null, access_token);

                originalRequest.headers.Authorization = `Bearer ${access_token}`;
                return apiClient(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError as Error, null);
                // Only nuke the session when the SERVER definitively
                // rejected the refresh token (401/403 response). A
                // network error here usually means the backend is
                // restarting (`python dev.py start`) or briefly
                // unreachable — clearing tokens in that case logged
                // the operator out on every dev restart. Keep the
                // tokens; the next request after the backend returns
                // will retry the refresh.
                const status = (refreshError as AxiosError)?.response?.status;
                if (status === 401 || status === 403) {
                    TokenManager.clear();
                    window.location.href = getUnauthorizedRedirectPath();
                }
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// ===========================================
// Response Unwrapping Helpers
// ===========================================

/**
 * Check if response is wrapped in backend format { success, data, message }
 */
function isBackendResponse<T>(data: unknown): data is BackendResponse<T> {
    return (
        typeof data === 'object' &&
        data !== null &&
        'success' in data &&
        'data' in data
    );
}

/**
 * Check if data contains paginated structure
 * Supports both formats:
 * - { pagination: { ... }, items: [...] }
 * - { total_properties: X, current_page: Y, properties: [...] }
 */
function isPaginatedData<T>(data: unknown): data is BackendPaginatedData<T> {
    if (typeof data !== 'object' || data === null) return false;

    // Format 1: { pagination: { ... } }
    if ('pagination' in data) return true;

    // Format 2: { total_properties, current_page, properties } (property-scraping format)
    if ('total_properties' in data && 'properties' in data) return true;

    // Format 3: { total, page, items } (generic format)
    if ('total' in data && ('items' in data || 'users' in data || 'contacts' in data)) return true;

    return false;
}

/**
 * Normalize paginated response from backend to frontend format
 * Handles multiple backend pagination formats
 */
function normalizePaginatedResponse<T>(data: BackendPaginatedData<T>): PaginatedResponse<T> {
    // Backend can return items under different keys: items, users, contacts, properties
    const items = data.items || data.users || data.contacts || data.properties || [];

    // Format 1: Standard pagination object
    if (data.pagination) {
        return {
            items: items as T[],
            total: data.pagination.total,
            page: data.pagination.page,
            per_page: data.pagination.per_page,
            total_pages: data.pagination.total_pages,
            has_next: data.pagination.has_next,
            has_prev: data.pagination.has_prev,
        };
    }

    // Format 2: property-scraping format (flat structure)
    if ('total_properties' in data) {
        const flatData = data as unknown as {
            total_properties: number;
            current_page: number;
            per_page: number;
            total_pages: number;
            has_next: boolean;
            has_prev: boolean;
            properties: T[];
        };
        return {
            items: flatData.properties as T[],
            total: flatData.total_properties,
            page: flatData.current_page,
            per_page: flatData.per_page,
            total_pages: flatData.total_pages,
            has_next: flatData.has_next,
            has_prev: flatData.has_prev,
        };
    }

    // Format 3: Generic flat format
    const genericData = data as unknown as {
        total?: number;
        page?: number;
        per_page?: number;
        total_pages?: number;
        has_next?: boolean;
        has_prev?: boolean;
    };
    return {
        items: items as T[],
        total: genericData.total || items.length,
        page: genericData.page || 1,
        per_page: genericData.per_page || items.length,
        total_pages: genericData.total_pages || 1,
        has_next: genericData.has_next || false,
        has_prev: genericData.has_prev || false,
    };
}

/**
 * Wrapper that carries pagination metadata beside its data array
 * Format: { success, data: [], total, skip, limit }
 */
interface WrapperPaginatedResponse<T> {
    data: T[];
    total: number;
    skip?: number;
    limit?: number;
    page?: number;
    per_page?: number;
}

/**
 * Check if wrapper response has pagination metadata alongside data array
 */
function isWrapperPaginated(data: unknown): data is WrapperPaginatedResponse<unknown> {
    if (typeof data !== 'object' || data === null) return false;

    return (
        'success' in data &&
        'data' in data &&
        Array.isArray((data as { data: unknown }).data) &&
        'total' in data
    );
}

/**
 * Normalize wrapper-level pagination
 */
function normalizeWrapperPagination<T>(wrapper: WrapperPaginatedResponse<T>): PaginatedResponse<T> {
    const items = wrapper.data;
    const total = wrapper.total;
    const perPage = wrapper.per_page || wrapper.limit || items.length;
    const page = wrapper.page || (wrapper.skip !== undefined ? Math.floor(wrapper.skip / perPage) + 1 : 1);
    const totalPages = perPage > 0 ? Math.ceil(total / perPage) : 1;

    return {
        items,
        total,
        page,
        per_page: perPage,
        total_pages: totalPages,
        has_next: page < totalPages,
        has_prev: page > 1,
    };
}

/**
 * Unwrap backend response and normalize if needed
 */
function unwrapResponse<T>(response: AxiosResponse): T {
    const data = response.data;

    // If wrapped in backend format, extract data
    if (isBackendResponse<T>(data)) {
        const innerData = data.data;

        // Check if wrapper itself has pagination metadata (e.g., contacts endpoint)
        // Format: { success, data: [], total, skip, limit }
        if (isWrapperPaginated(data)) {
            return normalizeWrapperPagination(data) as T;
        }

        // If inner data is paginated, normalize it
        if (isPaginatedData<T>(innerData)) {
            return normalizePaginatedResponse(innerData) as T;
        }

        return innerData;
    }

    // Not wrapped, return as-is
    return data;
}

// ===========================================
// Generic API Wrapper
// ===========================================
export const api = {
    get: async <T>(url: string, params?: Record<string, unknown>): Promise<T> => {
        const response = await apiClient.get(url, { params });
        return unwrapResponse<T>(response);
    },

    post: async <T>(url: string, data?: unknown): Promise<T> => {
        const response = await apiClient.post(url, data);
        return unwrapResponse<T>(response);
    },

    put: async <T>(url: string, data?: unknown): Promise<T> => {
        const response = await apiClient.put(url, data);
        return unwrapResponse<T>(response);
    },

    patch: async <T>(url: string, data?: unknown): Promise<T> => {
        const response = await apiClient.patch(url, data);
        return unwrapResponse<T>(response);
    },

    delete: async <T>(url: string): Promise<T> => {
        const response = await apiClient.delete(url);
        return unwrapResponse<T>(response);
    },
};

/**
 * Raw API methods (without unwrapping) - use when you need the full response
 */
export const apiRaw = {
    get: async <T>(url: string, params?: Record<string, unknown>): Promise<BackendResponse<T>> => {
        const response = await apiClient.get<BackendResponse<T>>(url, { params });
        return response.data;
    },

    post: async <T>(url: string, data?: unknown): Promise<BackendResponse<T>> => {
        const response = await apiClient.post<BackendResponse<T>>(url, data);
        return response.data;
    },
};

export { apiClient };
export default apiClient;
